import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Download, Search, RefreshCw, Info, ChevronLeft, ChevronRight } from 'lucide-react';
import { barrattAPI, type CallOffAggregatedData } from '../../lib/api/barratt';
import { csvBlob } from '../../utils/fileDownload';
import { toast } from 'sonner';

// Week bucket color thresholds (based on percentage of total for that row)
const getWeekCellColor = (count: number, totalInRow: number): string => {
  if (count === 0) return '';
  const percentage = (count / totalInRow) * 100;
  if (percentage >= 30) return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200';
  if (percentage >= 15) return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200';
  if (percentage >= 5) return 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200';
  return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200';
};

// Number of week columns to show at once
const WEEKS_PER_PAGE = 20;

export default function CallOffPerformance() {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<CallOffAggregatedData | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [weekPage, setWeekPage] = useState(0);

  // Fetch aggregated call-off performance data
  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await barrattAPI.getCallOffAggregatedData();
      setData(response.data);
    } catch (error) {
      console.error('Error fetching call off performance data:', error);
      toast.error('Failed to load call off performance data');
      // Set mock data for development/demo purposes
      setData({
        columns: ['Customer_Name', ...Array.from({ length: 40 }, (_, i) => `Week${i}`)],
        data: [],
        max_week: 39,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filter data by search term (customer name)
  const filteredData = useMemo(() => {
    if (!data?.data) return [];
    if (!searchTerm) return data.data;
    return data.data.filter((row) =>
      String(row['Customer_Name'] || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [data, searchTerm]);

  // Get week columns (excluding Customer_Name)
  const weekColumns = useMemo(() => {
    if (!data?.columns) return [];
    return data.columns.filter((col) => col.startsWith('Week'));
  }, [data]);

  // Paginate week columns
  const visibleWeekColumns = useMemo(() => {
    const start = weekPage * WEEKS_PER_PAGE;
    const end = start + WEEKS_PER_PAGE;
    return weekColumns.slice(start, end);
  }, [weekColumns, weekPage]);

  const totalWeekPages = Math.ceil(weekColumns.length / WEEKS_PER_PAGE);

  // Calculate row totals for color coding
  const getRowTotal = (row: Record<string, string | number>): number => {
    return weekColumns.reduce((sum, col) => sum + (Number(row[col]) || 0), 0);
  };

  // Export to CSV
  const handleExport = () => {
    if (!data?.data || data.data.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = ['Customer_Name', ...weekColumns];
    const csvContent = [
      headers.join(','),
      ...filteredData.map((row) =>
        headers.map((col) => {
          const value = row[col];
          // Escape quotes and wrap in quotes if contains comma
          const strValue = String(value ?? '');
          return strValue.includes(',') ? `"${strValue}"` : strValue;
        }).join(',')
      ),
    ].join('\n');

    const blob = csvBlob(csvContent); // CR-8703: BOM, so Excel reads UTF-8
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `call_off_performance_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Data exported successfully');
  };

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    if (!filteredData.length) return null;

    const totals: Record<string, number> = {};
    weekColumns.forEach((col) => {
      totals[col] = filteredData.reduce((sum, row) => sum + (Number(row[col]) || 0), 0);
    });

    const grandTotal = Object.values(totals).reduce((sum, val) => sum + val, 0);
    const avgPerCustomer = grandTotal / filteredData.length;

    // Find peak week (most deliveries)
    const peakWeek = Object.entries(totals).reduce(
      (max, [week, count]) => (count > max.count ? { week, count } : max),
      { week: 'Week0', count: 0 }
    );

    return {
      totalOrders: grandTotal,
      customerCount: filteredData.length,
      avgPerCustomer: Math.round(avgPerCustomer),
      peakWeek: peakWeek.week.replace('Week', ''),
      peakWeekCount: peakWeek.count,
    };
  }, [filteredData, weekColumns]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Call Off Performance</h1>
          <p className="text-muted-foreground">
            Weekly call-off performance by customer showing order counts per week bucket
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchData} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={!data?.data?.length}>
            <Download className="h-4 w-4 mr-2" />
            Export to CSV
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      {summaryStats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{summaryStats.totalOrders.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">Total Orders</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{summaryStats.customerCount}</div>
              <p className="text-xs text-muted-foreground">Customers</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{summaryStats.avgPerCustomer.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">Avg per Customer</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">Week {summaryStats.peakWeek}</div>
              <p className="text-xs text-muted-foreground">Peak Week</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{summaryStats.peakWeekCount.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">Peak Week Orders</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-center gap-2 flex-1">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by customer name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-sm"
              />
            </div>
            {weekColumns.length > WEEKS_PER_PAGE && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  Weeks {weekPage * WEEKS_PER_PAGE} - {Math.min((weekPage + 1) * WEEKS_PER_PAGE - 1, weekColumns.length - 1)}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setWeekPage((p) => Math.max(0, p - 1))}
                  disabled={weekPage === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setWeekPage((p) => Math.min(totalWeekPages - 1, p + 1))}
                  disabled={weekPage >= totalWeekPages - 1}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Performance Table with Week Columns */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle>Call Off Performance by Week</CardTitle>
            <Badge variant="outline">
              Max Week: {data?.max_week ?? 'N/A'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-12">
              <Info className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {data?.data?.length === 0
                  ? 'No call off performance data available. Upload supplier performance data to see results.'
                  : 'No customers match your search.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky left-0 bg-background z-10 min-w-[200px] border-r">
                      Customer Name
                    </TableHead>
                    <TableHead className="sticky left-[200px] bg-background z-10 min-w-[80px] text-right border-r">
                      Total
                    </TableHead>
                    {visibleWeekColumns.map((col) => (
                      <TableHead key={col} className="text-center min-w-[50px] px-1">
                        {col.replace('Week', 'W')}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredData.map((row, index) => {
                    const rowTotal = getRowTotal(row);
                    return (
                      <TableRow key={index}>
                        <TableCell className="sticky left-0 bg-background z-10 font-medium border-r">
                          {row['Customer_Name']}
                        </TableCell>
                        <TableCell className="sticky left-[200px] bg-background z-10 text-right font-semibold border-r">
                          {rowTotal.toLocaleString()}
                        </TableCell>
                        {visibleWeekColumns.map((col) => {
                          const count = Number(row[col]) || 0;
                          const cellColor = getWeekCellColor(count, rowTotal);
                          return (
                            <TableCell
                              key={col}
                              className={`text-center px-1 ${cellColor}`}
                            >
                              {count > 0 ? count : '-'}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                  {/* Totals row */}
                  <TableRow className="bg-muted/50 font-semibold">
                    <TableCell className="sticky left-0 bg-muted/50 z-10 border-r">
                      Total
                    </TableCell>
                    <TableCell className="sticky left-[200px] bg-muted/50 z-10 text-right border-r">
                      {filteredData.reduce((sum, row) => sum + getRowTotal(row), 0).toLocaleString()}
                    </TableCell>
                    {visibleWeekColumns.map((col) => {
                      const colTotal = filteredData.reduce((sum, row) => sum + (Number(row[col]) || 0), 0);
                      return (
                        <TableCell key={col} className="text-center px-1">
                          {colTotal > 0 ? colTotal : '-'}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}
          <div className="mt-4 text-sm text-muted-foreground">
            Showing {filteredData.length} of {data?.data?.length ?? 0} customers
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Understanding the Data</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-green-100 dark:bg-green-900/30 rounded border" />
              <span>High volume (30%+ of row)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-yellow-100 dark:bg-yellow-900/30 rounded border" />
              <span>Medium volume (15-30%)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-orange-100 dark:bg-orange-900/30 rounded border" />
              <span>Low volume (5-15%)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-red-100 dark:bg-red-900/30 rounded border" />
              <span>Very low (&lt;5%)</span>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            <strong>Week Bucket Calculation:</strong> Week number is calculated as the difference between
            the Required Date and Call Off Date, divided by 7. Week0 means delivery was required within
            0-6 days of call off, Week1 means 7-13 days, and so on.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
